/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as S from "./Admin.styles";

const API_BASE = "https://vip-resale.com";
const ADMIN_TOKEN_KEY = "vipResaleAdminToken";

type Product = {
  _id: string;
  name: string;
  description: string;
  price: number;
  isInstock: boolean;
  isPreOrder?: boolean;
  images?: string[];
  category?: string;
  gender?: "men" | "women" | "unisex";
  vintage?: boolean;
};

type ProductFormValues = {
  name: string;
  description: string;
  price: number;
  category: string;
  gender: string;
  vintage?: boolean;
  isInstock?: boolean;
  isPreOrder?: boolean;
};

function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

function buildProductFormData(values: ProductFormValues, files: UploadFile[]) {
  const formData = new FormData();
  formData.append("name", values.name);
  formData.append("description", values.description);
  formData.append("price", String(values.price));
  formData.append("category", values.category);
  formData.append("gender", values.gender);
  formData.append("vintage", String(!!values.vintage));
  formData.append("isPreOrder", String(!!values.isPreOrder));
  formData.append("isInstock", String(!values.isPreOrder && !!values.isInstock));

  files
    .map((file) => file.originFileObj)
    .filter(Boolean)
    .forEach((file) => formData.append("images", file as File));

  return formData;
}

function getProductStatus(product: Product) {
  if (product.isPreOrder) return "Під замовлення";
  return product.isInstock ? "In stock" : "Out of stock";
}

const ProductFields: React.FC<{
  fileList: UploadFile[];
  onUploadChange: UploadProps["onChange"];
  requireImages?: boolean;
}> = ({ fileList, onUploadChange, requireImages = false }) => (
  <>
    <Form.Item label="Name" name="name" rules={[{ required: true }]}>
      <Input />
    </Form.Item>

    <Form.Item label="Description" name="description" rules={[{ required: true }]}>
      <Input.TextArea rows={3} />
    </Form.Item>

    <Form.Item label="Price" name="price" rules={[{ required: true }]}>
      <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
    </Form.Item>

    <Form.Item label="Category" name="category" rules={[{ required: true }]}>
      <Select placeholder="Select category">
        <Select.Option value="shoes">Shoes</Select.Option>
        <Select.Option value="clothing">Clothing</Select.Option>
        <Select.Option value="accessories">Accessories</Select.Option>
      </Select>
    </Form.Item>

    <Form.Item label="Gender" name="gender" rules={[{ required: true }]}>
      <Select placeholder="Select gender">
        <Select.Option value="men">Men</Select.Option>
        <Select.Option value="women">Women</Select.Option>
        <Select.Option value="unisex">Unisex</Select.Option>
      </Select>
    </Form.Item>

    <Form.Item label="Vintage" name="vintage" valuePropName="checked">
      <Switch />
    </Form.Item>

    <Form.Item label="In Stock" name="isInstock" valuePropName="checked">
      <Switch />
    </Form.Item>

    <Form.Item
      label="Під замовлення"
      name="isPreOrder"
      valuePropName="checked"
      extra="Use this when the item is not in stock, but can be ordered."
    >
      <Switch />
    </Form.Item>

    <Form.Item
      label={requireImages ? "Images" : "New images"}
      required={requireImages}
      extra={requireImages ? undefined : "Leave empty to keep current images."}
    >
      <Upload
        multiple
        maxCount={10}
        beforeUpload={() => false}
        onChange={onUploadChange}
        fileList={fileList}
        listType="picture"
      >
        <Button icon={<UploadOutlined />}>Select Images</Button>
      </Upload>
    </Form.Item>
  </>
);

export const AdminLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values: { password: string }) => {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: values.password }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        message.error(data.error || "Login failed.");
        return;
      }

      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      navigate("/admin/products");
    } catch {
      message.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  if (getAdminToken()) return <Navigate to="/admin/products" replace />;

  return (
    <section css={S.page}>
      <Card title="Admin Login" css={S.loginCard}>
        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item label="Password" name="password" rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Login
          </Button>
        </Form>
      </Card>
    </section>
  );
};

export const ProtectedAdmin: React.FC = () => {
  const location = useLocation();

  if (!getAdminToken()) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/admin/login");
  };

  return (
    <section css={S.page}>
      <div css={S.shell}>
        <header css={S.header}>
          <div>
            <p css={S.kicker}>VIP Resale</p>
            <h1 css={S.title}>Admin</h1>
          </div>
          <nav css={S.nav}>
            <Link
              css={S.navLink(location.pathname === "/admin/products")}
              to="/admin/products"
            >
              Products
            </Link>
            <Link css={S.navLink(location.pathname === "/admin/add")} to="/admin/add">
              Add Product
            </Link>
            <Button onClick={logout}>Logout</Button>
          </nav>
        </header>
        <Outlet />
      </div>
    </section>
  );
};

export const AdminAddProduct: React.FC = () => {
  const [form] = Form.useForm<ProductFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  const onFinish = async (values: ProductFormValues) => {
    if (fileList.length === 0) {
      message.warning("Please upload at least one image.");
      return;
    }

    setSaving(true);

    try {
      const res = await fetch(`${API_BASE}/api/products`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        body: buildProductFormData(values, fileList),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        message.error(data.error || "Failed to create product.");
        return;
      }

      message.success("Product created.");
      form.resetFields();
      setFileList([]);
    } catch {
      message.error("Server error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Create Product" css={S.panel}>
      <Form
        layout="vertical"
        onFinish={onFinish}
        form={form}
        initialValues={{ vintage: false, isInstock: true, isPreOrder: false }}
        onValuesChange={(changed) => {
          if (changed.isPreOrder) form.setFieldValue("isInstock", false);
          if (changed.isInstock) form.setFieldValue("isPreOrder", false);
        }}
      >
        <ProductFields
          fileList={fileList}
          onUploadChange={(info) => setFileList(info.fileList)}
          requireImages
        />
        <Button type="primary" htmlType="submit" loading={saving}>
          Create
        </Button>
      </Form>
    </Card>
  );
};

export const AdminProducts: React.FC = () => {
  const [form] = Form.useForm<ProductFormValues>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editFiles, setEditFiles] = useState<UploadFile[]>([]);
  const [saving, setSaving] = useState(false);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/products`);
      if (!res.ok) throw new Error("Failed to fetch products");
      const data = await res.json();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const openEdit = (product: Product) => {
    setEditingProduct(product);
    setEditFiles([]);
    form.setFieldsValue({
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      gender: product.gender,
      vintage: product.vintage,
      isInstock: product.isInstock,
      isPreOrder: !!product.isPreOrder,
    });
  };

  const deleteProduct = async (productId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        message.error(data.error || "Failed to delete product.");
        return;
      }

      message.success("Product deleted.");
      setProducts((current) => current.filter((product) => product._id !== productId));
    } catch {
      message.error("Server error");
    }
  };

  const saveEdit = async () => {
    if (!editingProduct) return;

    try {
      const values = await form.validateFields();
      setSaving(true);

      const res = await fetch(`${API_BASE}/api/products/${editingProduct._id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
        body: buildProductFormData(values, editFiles),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        message.error(data.error || "Failed to update product.");
        return;
      }

      message.success("Product updated.");
      setProducts((current) =>
        current.map((product) => (product._id === data._id ? data : product))
      );
      setEditingProduct(null);
    } catch (err) {
      if (!(err instanceof Error)) return;
      message.error("Server error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin style={{ margin: 40 }} />;
  if (error) return <Alert type="error" message="Error" description={error} />;

  return (
    <>
      <Card title="Products" css={S.panel}>
        {products.length === 0 ? (
          <Empty description="No products" />
        ) : (
          <div css={S.grid}>
            {products.map((product) => (
              <article key={product._id} css={S.card}>
                <div css={S.imageWrap}>
                  {product.vintage && <span css={S.badge}>VINTAGE</span>}
                  {product.images?.[0] ? (
                    <img
                      css={S.image}
                      src={`${API_BASE}${product.images[0]}`}
                      alt={product.name}
                    />
                  ) : (
                    <div css={S.noImage}>No Image</div>
                  )}
                </div>
                <div css={S.cardBody}>
                  <div>
                    <h2 css={S.productName}>{product.name}</h2>
                    <p css={S.productMeta}>
                      {product.price}$ · {getProductStatus(product)}
                    </p>
                  </div>
                  <div css={S.actions}>
                    <Button onClick={() => openEdit(product)}>Edit</Button>
                    <Popconfirm
                      title="Delete product?"
                      okText="Delete"
                      cancelText="Cancel"
                      onConfirm={() => deleteProduct(product._id)}
                    >
                      <Button danger>Delete</Button>
                    </Popconfirm>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>

      <Modal
        title="Edit Product"
        open={!!editingProduct}
        onCancel={() => setEditingProduct(null)}
        onOk={saveEdit}
        confirmLoading={saving}
        okText="Save"
        width={720}
      >
        <Form
          layout="vertical"
          form={form}
          onValuesChange={(changed) => {
            if (changed.isPreOrder) form.setFieldValue("isInstock", false);
            if (changed.isInstock) form.setFieldValue("isPreOrder", false);
          }}
        >
          <ProductFields
            fileList={editFiles}
            onUploadChange={(info) => setEditFiles(info.fileList)}
          />
        </Form>
      </Modal>
    </>
  );
};
